import React, { useState, useEffect, useRef } from 'react';
import { PartnerShell, Kicker } from '../../components/partner/PartnerShell';
import { useParams } from 'react-router-dom';
import { getIntakeProfile, submitIntakeProfile, uploadIntakePhoto, uploadIntakeVideo } from '../../services/affiliateIntake.service';
import VideoDropZone from '../../components/common/VideoDropZone';

/**
 * The real form behind CB's "Your personalized 365reset landing page" email
 * — reached straight from that email link, no My Hub login required (Jenn,
 * 2026-09-10). Every field here matches his real copy exactly; nothing
 * invented. On submit, both videos push to Bunny Stream for encoding and a
 * real Team365 To-Do gets created automatically so the team knows to build
 * the page — the affiliate never needs to separately tell anyone they're done.
 */
export default function AffiliateIntake() {
  const { partnerId, token } = useParams();
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [name, setName] = useState('');
  const [photoUrl, setPhotoUrl] = useState('');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [story, setStory] = useState('');
  const [audience, setAudience] = useState('');
  const [quote, setQuote] = useState('');
  const [doNotMention, setDoNotMention] = useState('');
  // Partner Link tailoring questions (Jenn, 2026-10-09) — what Team365 writes their page words from.
  const [profession, setProfession] = useState('');
  const [clients, setClients] = useState('');
  const [clientNeeds, setClientNeeds] = useState('');
  const [tone, setTone] = useState('');
  const [hasLandingVideo, setHasLandingVideo] = useState(false);
  const [hasConfirmationVideo, setHasConfirmationVideo] = useState(false);
  const [videosJustUploaded, setVideosJustUploaded] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [isPartner, setIsPartner] = useState(false); // Partner Link wording (2026-10-07)
  const fileInputRef = useRef(null);

  useEffect(() => {
    getIntakeProfile(partnerId, token)
      .then(data => {
        setIsPartner(data.program === 'partner');
        setName(data.name || '');
        setPhotoUrl(data.photo_url || '');
        setStory(data.story_text || '');
        setAudience(data.audience_text || '');
        setQuote(data.quote_text || '');
        setDoNotMention(data.do_not_mention_text || '');
        setProfession(data.profession_text || '');
        setClients(data.clients_text || '');
        setClientNeeds(data.client_needs_text || '');
        setTone(data.tone_text || '');
        setHasLandingVideo(!!data.has_landing_video);
        setHasConfirmationVideo(!!data.has_confirmation_video);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [partnerId, token]);

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPhoto(true);
    setError('');
    try {
      const url = await uploadIntakePhoto(partnerId, token, file);
      setPhotoUrl(url);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not upload that photo.');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleVideoUpload = async (which, file) => {
    await uploadIntakeVideo(partnerId, token, which, file);
    setVideosJustUploaded(true);
    if (which === 'landing') setHasLandingVideo(true);
    else setHasConfirmationVideo(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      // Videos are already saved (uploaded straight to Bunny as soon as
      // they're dropped in) — submit only needs the text fields now.
      await submitIntakeProfile(partnerId, token, {
        storyText: story, audienceText: audience, quoteText: quote, doNotMentionText: doNotMention,
        ...(isPartner ? { professionText: profession, clientsText: clients, clientNeedsText: clientNeeds, toneText: tone } : {}),
      });
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save this — please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="min-h-screen bg-reset-cream" />;

  if (notFound) {
    return (
      <PartnerShell wide={false} label="365reset">
        <div className="bg-white rounded-2xl shadow-sm p-8 text-center">
          <h1 className="font-serif text-2xl font-bold text-reset-brown mb-3">This link isn't valid</h1>
          <p className="text-sm text-gray-500">Email <a href="mailto:team365@365reset.com" className="text-reset-terracotta font-bold underline">team365@365reset.com</a> and we'll send you a fresh one.</p>
        </div>
      </PartnerShell>
    );
  }

  if (submitted) {
    return (
      <PartnerShell wide={false} label={isPartner ? 'Partner Link' : 'Affiliate program'}>
        <div className="bg-white rounded-2xl shadow-sm p-8 text-center">
          <h1 className="font-serif text-2xl font-bold text-reset-brown mb-3">Thank you, {name.split(' ')[0]}!</h1>
          <p className="text-sm text-gray-500">
            We've got everything. We'll build your page and send it back to you for a look before it goes live.
            {videosJustUploaded && ' Your video(s) are encoding now — that takes a few minutes.'}
          </p>
        </div>
      </PartnerShell>
    );
  }

  const firstName = (name || '').split(' ')[0];
  return (
    <PartnerShell wide={false} label={isPartner ? 'Partner Link' : 'Affiliate program'}>
      {/* Jenn, 2026-10-09: branded, from Jenn, and never assume they remember
          they're getting their own page — say what this is and what happens next. */}
      <img src="/email-header-jenn.png" alt="Hey, it's Jenn — photo of Jenn Humphries, founder of 365reset"
        className="w-full rounded-2xl mb-6 bg-white" />
      <div className="bg-white rounded-2xl shadow-sm p-8 mb-6">
        <Kicker>{firstName ? `Welcome, ${firstName}` : 'Welcome'}</Kicker>
        <h1 className="font-serif text-3xl font-semibold text-reset-brownDeep leading-tight mb-4">
          Let's make your own 365reset page
        </h1>
        <p className="text-sm text-reset-brown leading-relaxed mb-3">
          {isPartner
            ? 'Thank you for partnering with 365reset. Every partner gets their own page to share with clients: your photo, a few words about you and your practice, and why you recommend 365reset, with a Join button that\'s connected to you.'
            : 'Thank you for being a 365reset affiliate. Every affiliate gets their own page to share: your photo, your story, and a Join button that\'s connected to you.'}
        </p>
        <p className="text-sm text-reset-brown leading-relaxed mb-5">
          This form gives us what we need to {isPartner ? 'write' : 'build'} it. It takes about 10 minutes, and nothing goes live until you've seen it.
        </p>
        <ol className="grid sm:grid-cols-4 gap-3 text-xs">
          {['You send this form', isPartner ? 'We write your page' : 'We build your page', 'You look it over', 'It goes live, and your dashboard shows your link'].map((step, i) => (
            <li key={step} className="bg-reset-cream rounded-xl p-3">
              <span className="block text-reset-terracotta font-bold mb-1">Step {i + 1}</span>
              <span className="text-reset-brown">{step}</span>
            </li>
          ))}
        </ol>
        <p className="text-xs text-gray-500 mt-5">
          This form comes from Jenn Humphries at 365reset. Questions? Email <a href="mailto:team365@365reset.com" className="text-reset-terracotta font-bold underline">team365@365reset.com</a>.
        </p>
      </div>
      <form onSubmit={handleSubmit} className="w-full bg-white rounded-2xl shadow-sm p-8">

        {error && <div className="mb-4 bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 text-sm">{error}</div>}

        <h2 className="text-xs font-bold uppercase tracking-wide text-gray-400 mb-3">About you</h2>

        <label className="block mb-4">
          <span className="block text-sm font-medium text-reset-brown mb-1">A photo of yourself you love</span>
          {photoUrl && <img src={photoUrl} alt="" className="w-20 h-20 rounded-full object-cover mb-2" />}
          <input type="file" accept="image/*" ref={fileInputRef} onChange={handlePhotoChange}
            className="block text-sm text-gray-500" />
          {uploadingPhoto && <span className="text-xs text-gray-400">Uploading…</span>}
        </label>

        <label className="block mb-4">
          <span className="block text-sm font-medium text-reset-brown mb-1">{isPartner ? 'One sentence about you and your practice' : 'How do you know 365reset, or why did you join?'}</span>
          <textarea value={story} onChange={e => setStory(e.target.value)} rows={2} required
            placeholder="One sentence is plenty"
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-reset-terracotta" />
        </label>

        <label className="block mb-4">
          <span className="block text-sm font-medium text-reset-brown mb-1">{isPartner ? 'Who will you share this page with?' : 'Who are you sending to this page?'}</span>
          <textarea value={audience} onChange={e => setAudience(e.target.value)} rows={2} required
            placeholder={isPartner ? 'e.g. "clients on my waitlist"' : 'e.g. "busy moms in perimenopause"'}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-reset-terracotta" />
        </label>

        <label className="block mb-4">
          <span className="block text-sm font-medium text-reset-brown mb-1">{isPartner ? 'Why do you recommend 365reset to your clients?' : "What's changed for you since joining 365reset?"}</span>
          <span className="block text-xs text-gray-400 mb-1">This becomes the quote on your page — 2-3 sentences</span>
          <textarea value={quote} onChange={e => setQuote(e.target.value)} rows={3} required
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-reset-terracotta" />
        </label>

        {isPartner && (
          <>
            <h2 className="text-xs font-bold uppercase tracking-wide text-gray-400 mb-1 mt-8">So we can write your page for your clients</h2>
            <p className="text-xs text-gray-500 mb-4">We'll write your page from these answers and send it to you for a look before it goes live.</p>
            {[
              [profession, setProfession, 'What do you do?', 'e.g. "Registered psychotherapist" or "Naturopath and yoga teacher"', 1, true],
              [clients, setClients, 'Who do you help?', 'e.g. "Women in midlife going through big changes"', 2, true],
              [clientNeeds, setClientNeeds, 'What do your clients find hard between sessions, that daily support could help with?', 'e.g. "Keeping up new habits once they leave my office"', 3, true],
              [tone, setTone, 'How would you like your page to sound?', 'e.g. "Warm and gentle", "Professional", "Down to earth"', 1, false],
            ].map(([value, setter, label, placeholder, rows, required]) => (
              <label key={label} className="block mb-4">
                <span className="block text-sm font-medium text-reset-brown mb-1">{label}</span>
                {!required && <span className="block text-xs text-gray-400 mb-1">Optional</span>}
                <textarea value={value} onChange={e => setter(e.target.value)} rows={rows} required={required}
                  placeholder={placeholder}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-reset-terracotta" />
              </label>
            ))}
          </>
        )}

        <label className="block mb-8">
          <span className="block text-sm font-medium text-reset-brown mb-1">Anything you'd rather we not mention or reference?</span>
          <span className="block text-xs text-gray-400 mb-1">Totally optional</span>
          <textarea value={doNotMention} onChange={e => setDoNotMention(e.target.value)} rows={2}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-reset-terracotta" />
        </label>

        <h2 className="text-xs font-bold uppercase tracking-wide text-gray-400 mb-3">Two short videos{isPartner ? ' (optional)' : ''}</h2>
        <p className="text-xs text-gray-500 mb-4">
          {isPartner && 'Your page works well without videos. If you\'d like to add them, a short hello helps clients feel they already know you. '}
          Record your video (landscape — like a Zoom call), then drag the video file below.
        </p>

        <label className="block mb-4">
          <span className="block text-sm font-medium text-reset-brown mb-1">Landing page video (10–30 sec)</span>
          <span className="block text-xs text-gray-400 mb-2">Your pitch to someone on the fence — why you love 365reset, who it's for, why they should check it out.</span>
          <VideoDropZone onUpload={(file) => handleVideoUpload('landing', file)} hasExisting={hasLandingVideo} />
        </label>

        <label className="block mb-8">
          <span className="block text-sm font-medium text-reset-brown mb-1">Welcome video (10–30 sec)</span>
          <span className="block text-xs text-gray-400 mb-2">Shown the moment someone joins — who you are, welcome them in, one thing to do first in the Hub.</span>
          <VideoDropZone onUpload={(file) => handleVideoUpload('confirmation', file)} hasExisting={hasConfirmationVideo} />
        </label>

        <button type="submit" disabled={submitting || uploadingPhoto}
          className="w-full px-6 py-3 bg-reset-terracotta text-white text-sm font-semibold rounded-xl hover:bg-reset-brown transition-colors disabled:opacity-50">
          {submitting ? 'Saving…' : 'Submit'}
        </button>
      </form>
    </PartnerShell>
  );
}
